<template>
  <div class="flex flex-wrap w-full flex-col laptop:flex-row laptop:pt-20 laptop:pl-20">
    <div class="h-250 w-full shadow overflow-hidden tablet:h-400 laptop:w-1/2 laptop:rounded 4k:h-600">
      <Photoswipe
        rotate
        class="h-full bg-gray-100"
      >
        <Transition name="transition-fade-in-medium">
          <UICarousel
            v-show="vehicleImages.length"
            class="h-full w-full bg-gray-100"
          >
            <UICarouselItem
              v-for="(imageSrc, idx) in vehicleImages"
              :key="`carousel-item-${idx}`"
            >
              <div
                :key="`image-${idx}`"
                v-pswp="imageSrc"
                :alt="`Image ${idx + 1}`"
                class="h-250 w-full bg-center bg-cover tablet:h-400 4k:h-600"
                :style="{ backgroundImage: `url('${imageSrc}')` }"
              ></div>
            </UICarouselItem>
            <template #prev>
              <div
                class="py-14 px-18 bg-black-10 rounded-r-lg select-none backdrop-filter-blur-2 transition-colors
                duration-100 hover:bg-black-45"
              >
                <IconFA
                  :icon="['far', 'chevron-left']"
                  class="text-xl text-white"
                />
              </div>
            </template>
            <template #next>
              <div
                class="py-14 px-18 bg-black-10 rounded-l-lg select-none backdrop-filter-blur-2 transition-colors
                duration-100 hover:bg-black-45"
              >
                <IconFA
                  :icon="['far', 'chevron-right']"
                  class="text-xl text-white"
                />
              </div>
            </template>
            <template #addons="{ activeIndex, itemCount }">
              <div
                class="absolute bottom-0 left-0 right-0 h-45 overlay-gradient pointer-events-none"></div>
              <div class="absolute bottom-0 left-0 text-white pb-10 pl-15">
                <span class="text-sm font-tabular-numbers">
                  {{ activeIndex + 1 }} / {{ itemCount }}
                </span>
              </div>
              <div class="absolute bottom-0 right-0 text-white pb-10 pr-15 pointer-events-none">
                <IconFA
                  :icon="['far', 'expand-wide']"
                  class="text-xl"
                />
              </div>
            </template>
          </UICarousel>
        </Transition>
      </Photoswipe>
    </div>
    <div class="w-full p-20 laptop:w-1/2 laptop:pt-3">
      <!-- For cars -->
      <template v-if="isCar && Object.keys(vehicleFilters).length">
        <h1
          v-if="mainTitle"
          class="font-semibold leading-tight tracking-wide mb-15"
        >
          {{ mainTitle }}
        </h1>
        <div
          v-for="carOption in carFieldOptions"
          :key="carOption.dbField"
          class="flex w-full"
          :class="carOption.dbField === 'notes'  || carOption.dbField === 'isNoWarranty' ? 'flex-col' : 'flex-row'"
        >
          <p
            v-if="(carOption.dbField !== 'images' && vehicleData[carOption.dbField])
            || carOption.dbField === 'validUntil' || carOption.dbField === 'registrationId'
            || carOption.dbField === 'isSummerWinterTyreSetAvailable'
            || carOption.dbField === 'isServiceDone'
            || carOption.dbField === 'mileage'
            || carOption.dbField === 'isTwoKeysAvailable'
            || carOption.dbField === 'availableFrom'
            || (carOption.dbField === 'isVehicleNotReadyToDrive' && vehicleData.isVehicleNotReadyToDrive)
            || (carOption.dbField === 'isFourWheelDrive' && !!vehicleData.isFourWheelDrive)"
            class="pb-10"
            :class="carOption.dbField === 'isNoWarranty'? 'w-full' : 'w-1/2'"
          >
            {{ $t(carOption.translationKey) }}
            <sup v-if="carOption.dbField === 'isNoWarranty'">1</sup>
          </p>
          <div
            :class="carOption.dbField === 'notes' || carOption.dbField === 'isNoWarranty' ? 'w-full' : 'w-1/2'">
            <p
              v-if="carOption.dbField === 'notes' && vehicleData[carOption.dbField]"
              class="font-bold whitespace-pre-line break-words"
            >{{ vehicleData[carOption.dbField] }}</p>
            <p
              v-if="carOption.dbField !== 'registrationId'"
              class="font-bold"
            >
              <template v-if="carOption.dbField === 'mileage'">
                {{ vehicleMileage.toLocaleString('de-CH') }}
              </template>
              <template v-else-if="carOption.dbField === 'gearboxType'">
                {{ vehicleGearboxType }}
              </template>
              <template v-else-if="carOption.dbField === 'fuelType'">
                {{ vehicleFuelType }}
              </template>
              <template
                v-else-if="carOption.dbField === 'bodyType' && vehicleData[carOption.dbField]">
                {{ vehicleBodyType }}
              </template>
              <template
                v-else-if="carOption.dbField === 'enginePower' && vehicleData[carOption.dbField]">
                {{ vehicleData[carOption.dbField].toLocaleString('de-CH') }}
                {{ $t('short.kilowatt') }}
              </template>
              <template v-else-if="carOption.dbField === 'engineCapacity'
            && vehicleData[carOption.dbField]">
                {{ vehicleData[carOption.dbField].toLocaleString('de-CH') }}
                cm<sup>3</sup>
              </template>
              <template
                v-else-if="carOption.dbField === 'enginePower' && vehicleData[carOption.dbField]">
                {{ Math.round(vehicleData.enginePower * 1.36) }}
              </template>
              <template v-else-if="carOption.dbField === 'registrationDate' && vehicleData[carOption.dbField]">
                {{ localDateFormat(vehicleData[carOption.dbField].seconds) }}
              </template>
              <template v-else-if="carOption.dbField === 'lastInspectionDate' && vehicleData[carOption.dbField]">
                {{ localDateFormat(vehicleData[carOption.dbField].seconds) }}
              </template>
              <template v-else-if="carOption.dbField === 'isSummerWinterTyreSetAvailable'">
                {{ checkedYesOrNo(carOption.dbField) }}
              </template>
              <template v-else-if="carOption.dbField === 'isServiceDone'">
                {{ checkedYesOrNo(carOption.dbField) }}
              </template>
              <template v-else-if="carOption.dbField === 'isTwoKeysAvailable'">
                {{ checkedYesOrNo(carOption.dbField) }}
              </template>
              <template v-else-if="carOption.dbField === 'isFourWheelDrive' && !!vehicleData.isFourWheelDrive">
                {{ checkedYesOrNo(carOption.dbField) }}
              </template>
              <template
                v-else-if="carOption.dbField === 'isVehicleNotReadyToDrive' && vehicleData.isVehicleNotReadyToDrive"
              >
                {{ checkedYesOrNo(carOption.dbField) }}
              </template>
              <template v-if="vehicle.availableFrom">
                <template v-if="carOption.dbField === 'availableFrom'
            && new Date(vehicle.availableFrom.seconds * 1000) <= new Date()">
                  {{ $t('vehicle_details.available_right_away_text') }}
                </template>
                <template v-else-if="carOption.dbField === 'availableFrom'
            && new Date(vehicle.availableFrom.seconds * 1000) > new Date()">
                  {{ localDateFormat(vehicle.availableFrom.seconds) }}
                </template>
              </template>
            </p>
            <span
              v-if="carOption.dbField === 'validUntil'"
              class="font-bold"
            >
              {{ validUntil }}
            </span>
            <a
              v-if="carOption.dbField === 'registrationId'"
              :href="idDocument"
              target="_blank"
              class="text-primary underline cursor-pointer"
            >
              <b>{{ $t('vehicle_details.open_document_text') }}</b>
            </a>
          </div>
        </div>
      </template>
      <!-- For Trucks -->
      <template v-else-if="!isCar">
        <h1 class="mb-10">
          <template v-if="vehicle.data">
          <span
            v-if="vehicle.data.truckMakeAndModel && vehicle.data.truckMakeAndModel.name"
            class="font-bold text-2xl"
          >
            {{ vehicle.data.truckMakeAndModel.name }}
          </span>
          <span v-if="vehicle.data.version">
            {{ vehicle.data.version }}
          </span>
          </template>
        </h1>
        <ul>
          <li
            v-for="option in truckOptions"
            :key="option.id"
            class="flex mb-10"
          >
            <p class="w-1/2 font-bold">{{ option.field }}</p>
            <p class="w-1/2">
              {{ option.value }}
              <template v-if="option.suffix">
                {{ option.suffix }}
              </template>
            </p>
          </li>
          <li
            v-show="truckOptions.length"
            class="flex"
          >
            <p class="w-1/2 font-bold">{{ $t('vehicle.id_document') }}</p>
            <p class="w-1/2">
              <a
                :href="idDocument"
                target="_blank"
                class="font-semibold text-primary underline cursor-pointer"
              >{{ $t('vehicle_details.open_document_text') }}</a>
            </p>
          </li>
        </ul>
        <div
          v-if="vehicleNote"
          class="w-full flex flex-col my-10"
        >
          <p class="font-bold leading-tight w-1/2">{{ $t('vehicle.notes') }}</p>
          <p class="leading-tight mt-5">{{ vehicleNote }}</p>
        </div>
      </template>
      <Transition name="transition-fade-in-medium">
        <div v-show="(type && !isCar) || (isCar && Object.keys(vehicleFilters).length)">
          <div
            v-if="userProfile.name || userProfile.city || userProfile.phoneNumber"
            class="w-full flex flex-col items-center justify-center bg-gray-100 shadow my-20 py-10 px-5 rounded"
          >
            <h2
              v-if="userProfile.name"
              class="font-semibold text-lg text-secondary-dark"
            >
              {{ userProfile.name }}
            </h2>
            <p
              v-if="userProfile.city"
              class="text-sm whitespace-pre">{{ userProfile.city }}</p>
            <p
              v-if="userProfile.phoneNumber"
              class="text-sm"
            >{{ userProfile.phoneNumber }}</p>
          </div>
          <BiddingSystemOld
            :buyer-id="$route.params.buyerId"
            :vehicle-id="$route.params.vehicleId"
            :vehicle-status="vehicle.status"
            :seller-public-profile="userProfile"
          />
          <p
            v-if="vehicle.data && vehicle.data.isNoWarranty"
            class="leading-tight text-xs mt-20"
          >
            <sup>1</sup>{{ $t('vehicle_details.is_no_warranty_explanation_text') }}
          </p>
        </div>
      </Transition>
    </div>
  </div>
</template>


<script>
import Vue from 'vue';
import Photoswipe from 'vue-pswipe';
import { defaultLocale } from '@/i18n';
import objectFieldByPath from '@/helpers/functions/objectFieldByPath';
import vehicleTypes from '@/helpers/vehicleTypes';
import UICarousel from '@/components/UI-old/carousel/UICarousel';
import UICarouselItem from '@/components/UI-old/carousel/UICarouselItem';
import BiddingSystemOld from '@/components/BiddingSystemOld';


const photoswipeConfig = {
  shareEl: false,
  fullscreenEl: false,
  tapToClose: true,
  counterEl: true,
  arrowEl: true,
  zoomEl: false,
  bgOpacity: 0.8,
};
Vue.use(Photoswipe, photoswipeConfig);


const truckFieldOptions = [{
  fieldPath: 'validUntil',
  translationPath: 'vehicle.valid_until',
  formatValue: (value, locale) => value.toDate().toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }),
}, {
  fieldPath: 'availableFrom',
  translationPath: 'vehicle.available_from',
  formatValue: (value, locale) => value.toDate().toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }),
}, {
  fieldPath: 'data.truckBodyType',
  translationPath: 'vehicle.body_type',
}, {
  fieldPath: 'data.truckAxeType',
  translationPath: 'vehicle.axe_type',
}, {
  fieldPath: 'data.enginePower',
  translationPath: 'vehicle.engine_power',
  translationPathSuffix: 'short.horsepower',
}, {
  fieldPath: 'data.truckEmissionType',
  translationPath: 'vehicle.emission_type',
}, {
  fieldPath: 'data.gearboxType',
  translationPath: 'vehicle.gearbox_type',
}, {
  fieldPath: 'data.vehicleTiresCondition',
  translationPath: 'vehicle.tires_condition',
}, {
  fieldPath: 'data.vehicleCondition',
  translationPath: 'vehicle.general_condition',
}];


export default {
  props: {
    locale: {
      type: String,
      default: defaultLocale,
    },
    idDocument: {
      type: String,
      default: '',
    },
    userProfile: {
      type: Object,
      default: () => {
      },
    },
    type: {
      type: String,
      validator: (value) => Object.values(vehicleTypes).includes(value),
    },
    vehicle: {
      type: Object,
      default: () => {
      },
    },
    vehicleFilters: {
      type: Object,
      default: () => {
      },
    },
    vehicleData: Object,
    vehicleImages: Array,
    validUntil: String,
  },
  components: {
    UICarousel,
    UICarouselItem,
    BiddingSystemOld,
  },
  data: () => ({
    carFieldOptions: Object.freeze([{
      dbField: 'validUntil',
      translationKey: 'vehicle_details.valid_until_text',
    }, {
      dbField: 'mileage',
      translationKey: 'vehicle.mileage',
    }, {
      dbField: 'bodyType',
      translationKey: 'vehicle.body_type',
    }, {
      dbField: 'registrationDate',
      translationKey: 'vehicle.registration_date',
    }, {
      dbField: 'lastInspectionDate',
      translationKey: 'vehicle_details.mfk_text',
    }, {
      dbField: 'fuelType',
      translationKey: 'vehicle.fuel_type',
    }, {
      dbField: 'gearboxType',
      translationKey: 'vehicle.gearbox_type',
    }, {
      dbField: 'enginePower',
      translationKey: 'vehicle.engine_power',
    }, {
      translationKey: 'short.horsepower',
    }, {
      dbField: 'engineCapacity',
      translationKey: 'vehicle.engine_capacity',
    }, {
      dbField: 'images',
      multipleFiles: true,
      acceptType: '.jpg, .jpeg, .png',
      fileType: 'image',
      translationKey: 'vehicle_details.images_text',
    }, {
      dbField: 'availableFrom',
      translationKey: 'vehicle.available_from',
    }, {
      dbField: 'registrationId',
      acceptType: '.pdf, .jpg, .jpeg, .png',
      fileType: 'document',
      translationKey: 'vehicle.id_document',
    }, {
      dbField: 'isFourWheelDrive',
      translationKey: 'vehicle.options.four_wheel_drive',
    }, {
      dbField: 'isSummerWinterTyreSetAvailable',
      translationKey: 'vehicle.options.summer_winter_tyre_set_available',
    }, {
      dbField: 'isServiceDone',
      translationKey: 'vehicle.options.service_done',
    }, {
      dbField: 'isTwoKeysAvailable',
      translationKey: 'vehicle.options.two_keys_available',
    }, {
      dbField: 'isVehicleNotReadyToDrive',
      translationKey: 'vehicle.options.not_ready_to_drive',
    }, {
      dbField: 'isNoWarranty',
      translationKey: 'vehicle.options.no_warranty',
    }, {
      dbField: 'notes',
      translationKey: 'vehicle.notes',
    }]),
  }),
  computed: {
    isCar() {
      return this.type === vehicleTypes.CAR;
    },
    truckOptions() {
      if (Object.keys(this.vehicle?.data || {}).length === 0 || this.isCar) return [];

      return truckFieldOptions.reduce((fields, option) => {
        const { fieldPath } = option;
        const vehicleField = objectFieldByPath(this.vehicle, option.fieldPath);
        let fieldValue = '';

        if (!vehicleField) return fields;

        if (vehicleField[this.$i18n.locale]) {
          fieldValue = vehicleField[this.$i18n.locale].name;
        } else if (vehicleField.name) {
          fieldValue = vehicleField.name;
        } else if (vehicleField) {
          fieldValue = vehicleField;
        }

        if (option.formatValue) {
          fieldValue = option.formatValue(fieldValue, this.locale);
        }

        fields.push({
          id: fieldPath,
          field: this.$t(option.translationPath),
          value: fieldValue,
          ...option.translationPathSuffix && { suffix: this.$t(option.translationPathSuffix) },
        });

        return fields;
      }, []);
    },
    vehicleMakeAndModel() {
      if (!this.isCar) return '';
      if (Object.keys(this.vehicleFilters).length === 0 || !this.vehicleData.make || !this.vehicleData.model) return '';

      const vehicleMakeWithModels = this.vehicleFilters.makeAndModel
        .find((option) => option.value === this.vehicleData.make);

      return {
        makeName: vehicleMakeWithModels.name,
        modelName: vehicleMakeWithModels.subOptions
          .find((subOption) => subOption.value === this.vehicleData.model).name,
      };
    },
    vehicleMileage() {
      if (!this.isCar) return '';
      return `${this.vehicle?.data?.mileage.toLocaleString('de-CH')} km`;
    },
    vehicleGearboxType() {
      if (!this.isCar) return '';
      if (Object.keys(this.vehicleFilters).length === 0 || !this.vehicleData.gearboxType) return '';

      const gearOption = this.vehicleFilters.gearboxType
        .find((option) => option.value === this.vehicleData.gearboxType);

      if (!gearOption[this.$i18n.locale]) return '';

      return gearOption[this.$i18n.locale].name;
    },
    vehicleFuelType() {
      if (!this.isCar) return '';
      if (Object.keys(this.vehicleFilters).length === 0 || !this.vehicleData.fuelType) return '';

      const fuelOption = this.vehicleFilters.fuelType
        .find((option) => option.value === this.vehicleData.fuelType);

      if (!fuelOption[this.$i18n.locale]) return '';

      return fuelOption[this.$i18n.locale].name;
    },
    vehicleBodyType() {
      if (!this.isCar) return '';
      if (Object.keys(this.vehicleFilters).length === 0 || !this.vehicleData.bodyType) return '';

      const fuelOption = this.vehicleFilters.bodyType
        .find((option) => option.value === this.vehicleData.bodyType);

      if (!fuelOption[this.$i18n.locale]) return '';

      return fuelOption[this.$i18n.locale].name;
    },
    mainTitle() {
      if (!this.isCar) return '';
      if (!this.vehicleMakeAndModel.makeName) return '';

      return `${this.vehicleMakeAndModel.makeName} ${this.vehicleMakeAndModel.modelName} ${this.vehicleData.version}`;
    },
    vehicleNote() {
      if (!this.vehicle.data) return;
      return this.vehicle.data.notes;
    },
  },
  methods: {
    localDateFormat(date) {
      return new Date(date * 1000).toLocaleDateString(this.locale);
    },
    checkedYesOrNo(field) {
      let text = '';
      if (this.vehicleData[field]) {
        text = this.$t('vehicle_details.is_checked_text');
      } else {
        text = this.$t('vehicle_details.is_not_checked_text');
      }
      return text;
    },
  },
};
</script>


<style scoped>
.prev, .next {
  top: calc(50% - theme('spacing.22'));
  transition: 0.6s ease;
}

/* Handling environments that have a precise pointing device */
@media (any-pointer: fine) {
  .prev:hover, .next:hover {
    background-color: rgba(0, 0, 0, 0.5);
  }
}
</style>
